import React, { useState } from 'react';

// Lightweight SVG donut. `segments`: [{ label, value, color }] (value = weight%).
// Hovering a slice (ring segment OR its legend row) highlights that slice and
// shows its label + share in the center; otherwise the center shows `centerLabel`.
export default function Donut({ segments, centerLabel, size = 150 }) {
  const [hover, setHover] = useState(null);
  const list = segments || [];
  const total = list.reduce((s, x) => s + (x.value || 0), 0) || 1;

  const C = 2 * Math.PI * 40;
  let off = 0;
  const arcs = list.map((seg) => {
    const len = (seg.value / total) * C;
    const arc = { ...seg, len, off };
    off += len;
    return arc;
  });

  const active = hover != null ? list[hover] : null;
  const activePct = active ? ((active.value / total) * 100).toFixed(1) : null;

  return (
    <div className="flex flex-col items-center w-full">
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 120 120" width={size} height={size}>
          <circle cx="60" cy="60" r="40" fill="none" strokeWidth="16" stroke="currentColor" className="text-gray-100 dark:text-[#20272f]" />
          {arcs.map((a, i) => (
            <circle
              key={i}
              cx="60" cy="60" r="40" fill="none"
              strokeWidth={hover === i ? 19 : 16}
              stroke={a.color}
              strokeOpacity={hover == null || hover === i ? 1 : 0.35}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={-a.off}
              transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-width .12s, stroke-opacity .12s', cursor: 'pointer' }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            />
          ))}
        </svg>
        <div className="absolute inset-0 grid place-items-center text-center pointer-events-none px-4">
          {active ? (
            <div>
              <div className="text-[10px] text-gray-500 dark:text-gray-300 truncate max-w-[110px]" title={active.label}>{active.label}</div>
              <div className="mt-0.5 font-mono text-[15px] text-gray-800 dark:text-gray-100">{activePct}%</div>
            </div>
          ) : centerLabel ? (
            <div className="text-[9px] uppercase tracking-widest text-gray-400">{centerLabel}</div>
          ) : null}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[10.5px] w-full">
        {list.map((s, i) => (
          <button
            key={i}
            type="button"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            className={`flex items-center justify-between gap-2 min-w-0 text-left rounded px-1 -mx-1 transition-colors ${hover === i ? 'bg-gray-100 dark:bg-[#20272f]' : ''}`}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="inline-block w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} />
              <span className="truncate text-gray-600 dark:text-gray-300" title={s.label}>{s.label}</span>
            </span>
            <span className="tabular-nums text-gray-500 shrink-0">{((s.value / total) * 100).toFixed(1)}%</span>
          </button>
        ))}
      </div>
    </div>
  );
}
